import React, { useEffect, useState, useRef, useCallback } from "react";
import { Outlet } from "react-router-dom";

import AthleteSidebar from "./AthleteSidebar";
import TermsAndConditionsModal from "./TermsAndConditionsModal";
import TeamChatOverlay from "./team-chat/TeamChatOverlay";
import { API_BASE_URL } from "../utils/apiConfig";
import { getTermsAccepted, getHasProfile, isGuardian, isAthlete } from "../utils/auth";
import { athleteProfileFetchPath, ensureGuardianViewingAthleteId, getGuardianViewingAthleteId } from "../utils/viewedAthlete";
import { useAuth } from "../contexts/AuthContext";
import {
    ATHLETE_ONBOARDING_UPDATED_EVENT,
    fetchAthleteOnboardingStatus,
} from "../services/athleteOnboardingService";
import { fetchCompanyLogoBlobUrl, useMyAvatar, useUserAvatar } from "../utils/profilePictureUtils";

import "../styles/layout.css";
import "../styles/athlete-self-page-layout.css";
import { apiClient } from "../utils/apiClient";

export default function AthleteLayout() {
    const [hasProfile, setHasProfile] = useState(false);
    const [bannerAthleteUserId, setBannerAthleteUserId] = useState<number | undefined>(undefined);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [companyLogo, setCompanyLogo] = useState<string | null>(null);
    const [companyLogoVersion, setCompanyLogoVersion] = useState(0);

    const isFetchingRef = useRef(false);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [showOnboardingNav, setShowOnboardingNav] = useState(true);

    const { userInfo, isAuthenticated } = useAuth();

    const guardianAthleteId = localStorage.getItem("guardianViewingAthleteId");
    const isGuardianViewing = isGuardian() && !!guardianAthleteId;
    const useGuardianRail = isGuardian() && !isAthlete();
    const useAthleteRail = isAthlete();
    const useRailLayout = useGuardianRail || useAthleteRail;
    const termsAccepted = getTermsAccepted() || !!userInfo?.termsAndConditionsAccepted;

    const { avatarUrl: myAvatarUrl } = useMyAvatar(
        !isGuardianViewing && isAuthenticated && hasProfile && termsAccepted
    );
    const { avatarUrl: viewedAthleteAvatarUrl } = useUserAvatar(
        isGuardianViewing ? bannerAthleteUserId : undefined
    );
    const sidebarAvatarUrl = isGuardianViewing ? viewedAthleteAvatarUrl : myAvatarUrl;

    useEffect(() => {
        if (!isAuthenticated) {
            setIsTermsModalOpen(false);
            return;
        }
        const hasAcceptedTerms = getTermsAccepted() || userInfo?.termsAndConditionsAccepted;
        setIsTermsModalOpen(!hasAcceptedTerms);
    }, [isAuthenticated, userInfo?.termsAndConditionsAccepted]);

    const authTermsAcceptedRef = useRef(userInfo?.termsAndConditionsAccepted ?? false);
    authTermsAcceptedRef.current = userInfo?.termsAndConditionsAccepted ?? false;

    const fetchAthleteData = useCallback(async () => {
        if (isFetchingRef.current) {
            return;
        }

        const hasAcceptedTerms = getTermsAccepted() || authTermsAcceptedRef.current;
        if (!hasAcceptedTerms) {
            return;
        }

        isFetchingRef.current = true;

        try {
            // Use the shared resolver so a single-athlete guardian still resolves when no explicit
            // selection is persisted (avoids falling through to /api/athletes/me, which 404s for a
            // guardian who has no athlete profile of their own).
            if (isGuardian() && !getGuardianViewingAthleteId()) {
                ensureGuardianViewingAthleteId();
            }
            const profilePath = athleteProfileFetchPath();
            if (!profilePath) {
                return;
            }
            const url = `${API_BASE_URL}${profilePath}?t=${Date.now()}`;

            const res = await apiClient(url, {
                method: "GET",
                headers: { Accept: "application/json" },
                credentials: "include",
                cache: "no-cache",
            });

            if (!res.ok) {
                return;
            }

            const j: any = await res.json();
            const athleteUserId = typeof j?.userId === "number" ? j.userId : undefined;
            const userHasProfile = getHasProfile();

            setHasProfile((prev) => (prev !== userHasProfile ? userHasProfile : prev));
            setBannerAthleteUserId((prev) => (prev !== athleteUserId ? athleteUserId : prev));
        } finally {
            isFetchingRef.current = false;
        }
    }, []);

    const refreshCompanyLogoBadge = useCallback(async () => {
        try {
            if (!isAuthenticated) {
                setCompanyLogo(null);
                return;
            }

            const profileRes = await apiClient(`${API_BASE_URL}/api/admin-profile`, {
                method: "GET",
                headers: { Accept: "application/json" },
                credentials: "include",
            });

            if (!profileRes.ok) {
                setCompanyLogo(null);
                return;
            }

            const profile: { email?: string; username?: string } = await profileRes.json();
            const cacheKey = profile?.email ?? profile?.username ?? "";
            if (!cacheKey) {
                setCompanyLogo(null);
                return;
            }

            const logoBlob = await fetchCompanyLogoBlobUrl(cacheKey, false);
            setCompanyLogo(logoBlob);
            setCompanyLogoVersion((prev) => prev + 1);
        } catch {
            setCompanyLogo(null);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchAthleteData();
    }, [fetchAthleteData, refreshKey]);

    useEffect(() => {
        if (userInfo?.termsAndConditionsAccepted && userInfo?.authenticated) {
            fetchAthleteData();
            refreshCompanyLogoBadge();
        }
    }, [userInfo?.termsAndConditionsAccepted, userInfo?.authenticated, fetchAthleteData, refreshCompanyLogoBadge]);

    useEffect(() => {
        if (getTermsAccepted()) {
            refreshCompanyLogoBadge();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleProfileUpdate = () => {
            setTimeout(() => {
                refreshCompanyLogoBadge();
            }, 1000);
        };
        window.addEventListener("profileUpdated", handleProfileUpdate);
        return () => window.removeEventListener("profileUpdated", handleProfileUpdate);
    }, [refreshCompanyLogoBadge]);

    useEffect(() => {
        const handleProfileUpdate = () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                setRefreshKey((prev) => prev + 1);
            }, 300);
        };

        window.addEventListener("athleteProfileUpdated", handleProfileUpdate);
        window.addEventListener("profilePictureUpdated", handleProfileUpdate);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            window.removeEventListener("athleteProfileUpdated", handleProfileUpdate);
            window.removeEventListener("profilePictureUpdated", handleProfileUpdate);
        };
    }, []);

    useEffect(() => {
        const handleTermsAccepted = () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                setRefreshKey((prev) => prev + 1);
                refreshCompanyLogoBadge();
            }, 300);
        };

        window.addEventListener("termsAccepted", handleTermsAccepted);
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            window.removeEventListener("termsAccepted", handleTermsAccepted);
        };
    }, [refreshCompanyLogoBadge]);

    const refreshOnboardingNav = useCallback(async () => {
        if (!isAthlete()) {
            setShowOnboardingNav(true);
            return;
        }

        const status = await fetchAthleteOnboardingStatus();
        setShowOnboardingNav(status?.completed !== true);
    }, []);

    useEffect(() => {
        if (userInfo?.termsAndConditionsAccepted && userInfo?.authenticated && isAthlete()) {
            void refreshOnboardingNav();
        }
    }, [userInfo?.termsAndConditionsAccepted, userInfo?.authenticated, refreshOnboardingNav]);

    useEffect(() => {
        const handleOnboardingUpdated = () => {
            void refreshOnboardingNav();
        };
        window.addEventListener(ATHLETE_ONBOARDING_UPDATED_EVENT, handleOnboardingUpdated);
        return () => window.removeEventListener(ATHLETE_ONBOARDING_UPDATED_EVENT, handleOnboardingUpdated);
    }, [refreshOnboardingNav]);

    const handleCompanyLogoError = () => {
        setCompanyLogo(null);
    };

    return (
        <div className={`app ${useRailLayout ? "with-rail-sidebar" : ""}`}>
            <AthleteSidebar
                variant={useGuardianRail ? "guardian-rail" : "athlete"}
                companyLogoUrl={useRailLayout ? companyLogo : null}
                companyLogoVersion={companyLogoVersion}
                footerAvatarUrl={useRailLayout ? sidebarAvatarUrl : null}
                onCompanyLogoError={handleCompanyLogoError}
                showOnboardingNav={showOnboardingNav}
            />
            <main className="content">
                <Outlet />
            </main>

            <TermsAndConditionsModal
                open={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
            />
            <TeamChatOverlay />
        </div>
    );
}
